import React from "react";
import { motion } from "framer-motion";
import { X, Microphone, Waveform } from "@phosphor-icons/react";
import { ASSETS } from "@/lib/assets";

const LABELS = {
  listening: ["Listening… just talk", "Pause when you're done, or tap Nick to send now"],
  transcribing: ["Got it…", "Turning that into text"],
  thinking: ["Nick is thinking…", "Hang tight"],
  speaking: ["Nick is talking", "Tap Nick to interrupt"],
  paused: ["Paused", "Tap Nick to keep talking"],
};

function Orb({ state, level, onTap }) {
  const busy = state === "thinking" || state === "transcribing";
  const ringScale = state === "listening" ? 1 + level * 0.45 : 1;
  return (
    <button onClick={onTap} data-testid="voice-orb" aria-label="Voice control" className="relative w-20 h-20 shrink-0 rounded-full focus:outline-none">
      <motion.span
        className={`absolute inset-0 rounded-full ${state === "speaking" ? "bg-[#73bf44]/30" : "bg-[#73bf44]/15"}`}
        animate={state === "speaking" ? { scale: [1, 1.18, 1] } : { scale: ringScale }}
        transition={state === "speaking" ? { duration: 1.1, repeat: Infinity } : { duration: 0.08 }}
      />
      {busy && <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#73bf44] animate-spin" />}
      <span className={`absolute inset-2 rounded-full overflow-hidden border-2 ${state === "paused" ? "border-neutral-600 grayscale" : "border-[#73bf44]"}`}>
        <img src={ASSETS.nick} alt="Nick" className="w-full h-full object-cover" />
      </span>
    </button>
  );
}

export function VoicePanel({ voice, statusText }) {
  const [title, sub] = LABELS[voice.state] || LABELS.listening;
  const shownTitle = voice.state === "thinking" && statusText ? statusText : title;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-3xl mx-auto bg-[#121212] border border-[#73bf44]/40 rounded-2xl px-4 py-3 flex items-center gap-4"
      data-testid="voice-panel"
    >
      <Orb state={voice.state} level={voice.level} onTap={voice.tap} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-white font-heading font-semibold" data-testid="voice-state-label">
          {voice.state === "listening" ? <Microphone size={16} className="text-[#73bf44]" weight="fill" /> : <Waveform size={16} className="text-[#73bf44]" />}
          <span className="truncate">{shownTitle}</span>
        </div>
        <div className="text-xs text-neutral-500 mt-0.5">{sub}</div>
        {voice.heard && (
          <div className="text-xs text-neutral-400 mt-1.5 truncate italic" data-testid="voice-heard">“{voice.heard}”</div>
        )}
      </div>
      <button
        onClick={voice.stop}
        data-testid="voice-end-button"
        className="shrink-0 flex items-center gap-1.5 text-sm text-neutral-300 border border-neutral-700 rounded-full px-3 py-1.5 hover:border-red-500/60 hover:text-white transition-colors"
      >
        <X size={14} /> End
      </button>
    </motion.div>
  );
}
